/**
 * CLI error handling utilities.
 */

import { formatError } from './format.js';

/**
 * Display error and exit
 */
export function cliError(message: string, exitCode = 1): never {
  console.error(formatError(message));
  process.exit(exitCode);
}

/**
 * Handle API errors and convert to CLI messages
 */
export function handleApiError(error: any): never {
  if (!error) {
    cliError('Unknown error occurred', 1);
  }

  // Handle authentication errors
  if (error.message?.includes('no authentication token found')) {
    cliError(
      'Authentication required. Run: hypertask auth login --token <jwt>\nOr set HYPERTASKS_JWT_TOKEN environment variable',
      1
    );
  }

  //Handle specific section not found error
  if (error.message?.includes('Section')) {
    cliError(error.message, 1);
  }

  if (error.message?.includes('401') || error.code === 401) {
    cliError(
      'Authentication failed. Token may be expired. Run: hypertask auth login --token <jwt>',
      1
    );
  }

  // Handle validation errors
  if (error.message?.includes('Validation') || error.code === -32602) {
    cliError(`Invalid input: ${error.message}`, 2);
  }

  // Handle not found errors
  if (error.message?.includes('404') || error.code === 404) {
    cliError(`Resource not found: ${error.message}`, 3);
  }

  // Handle general API errors
  if (error.message) {
    cliError(`API error: ${error.message}`, 4);
  }

  cliError('API error occurred', 4);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== '{}') {
      return serialized;
    }
  } catch {
    // Fall through to the generic message.
  }

  return 'Unknown attachment error';
}

export function buildAttachFailureMessage(
  ticket: string,
  taskId: number | undefined,
  error: unknown
): string {
  const cleanTicket = ticket.trim();
  const createdTask = cleanTicket
    ? `Task ${cleanTicket}`
    : taskId != null
      ? `Task id ${taskId}`
      : 'The task';
  const retryTarget = cleanTicket || '<ticket>';
  const retryLead = cleanTicket
    ? 'Retry the attachment with:'
    : taskId != null
      ? `Find the ticket number for task id ${taskId}, then retry the attachment with:`
      : 'Find the task ticket number, then retry the attachment with:';

  return [
    `${createdTask} was created, but attaching files failed: ${getErrorMessage(error)}`,
    `${retryLead} hypertask task update ${retryTarget} --attach <file>`,
  ].join('\n');
}

/**
 * Validate required option
 */
export function requireOption(value: any, name: string): string {
  if (!value) {
    cliError(`Required option: --${name.replace(/([A-Z])/g, '-$1').toLowerCase()}`);
  }
  return value;
}

/**
 * Parse comma-separated list
 */
export function parseList(value: string): string[] {
  if (!value) return [];
  return value.split(',').map(v => v.trim()).filter(v => v);
}

/**
 * Parse numeric value
 */
export function parseNumber(value: string, name: string): number {
  const num = parseInt(value, 10);
  if (isNaN(num)) {
    cliError(`Invalid number for --${name}: ${value}`);
  }
  return num;
}

export const MAX_PAGE_LIMIT = 100;

/**
 * Parse --limit for paginated list commands (max 100 per API).
 */
export function parsePageLimit(value: string): number {
  const num = parseNumber(value, 'limit');
  if (num > MAX_PAGE_LIMIT) {
    cliError(`--limit cannot exceed ${MAX_PAGE_LIMIT} (got ${num})`);
  }
  return num;
}

const TICKET_RE = /^[A-Za-z]+-\d+$/;

export function validateTicketNumber(ticket: string): void {
  if (!TICKET_RE.test(ticket)) {
    cliError(
      `Invalid ticket number "${ticket}". Expected format: PREFIX-NNN (e.g. HTPR-42)`
    );
  }
}
